import { forwardRef, InputHTMLAttributes } from 'react';

/**
 * UiCheckbox - Standardized native checkbox
 *
 * Renders the browser-native widget and colours it with `accent-color`, which
 * is the standard CSS hook for theming native controls.
 *
 * Size and accent are props rather than `className` overrides on purpose: the
 * project has no `clsx` / `tailwind-merge`, so a caller-supplied `w-[25px]`
 * would collide with the base `w-[13px]` at equal specificity and the winner
 * would be decided by generated-CSS order.
 */

export type UiCheckboxSize = 'sm' | 'md' | 'lg';
export type UiCheckboxAccent = 'primary' | 'success' | 'none';

export interface UiCheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  /** Box size — sm 13px (default), md 17px, lg 25px */
  size?: UiCheckboxSize;
  /**
   * Accent colour. `primary` is the theme-aware blue; `success` is #5cb85c,
   * matching the green the legacy `.checkbox-success` wrapper used to draw.
   *
   * Use `none` to emit no accent class at all, so a caller supplying its own
   * arbitrary accent utility through `className` wins. Without it the base
   * class and the caller's sit at equal specificity and generated-CSS order
   * decides — which silently favours the base.
   *
   * (Written without an example class on purpose: Tailwind scans comments too,
   * and a literal utility here would be compiled into the bundle.)
   */
  accent?: UiCheckboxAccent;
  /** Additional CSS classes */
  className?: string;
}

const SIZE_CLASSES: Record<UiCheckboxSize, string> = {
  sm: 'w-[13px] h-[13px]',
  md: 'w-[17px] h-[17px]',
  lg: 'w-[25px] h-[25px]',
};

const ACCENT_CLASSES: Record<UiCheckboxAccent, string> = {
  primary: 'accent-angular-blue',
  success: 'accent-[#5cb85c]',
  none: '',
};

export const UiCheckbox = forwardRef<HTMLInputElement, UiCheckboxProps>(
  ({ className = '', size = 'sm', accent = 'primary', ...props }, ref) => {
    const baseClasses =
      'rounded border-angular-border focus:ring-angular-focus-ring disabled:cursor-not-allowed disabled:bg-angular-input-disabled-bg';

    return (
      <input
        ref={ref}
        type='checkbox'
        className={[baseClasses, SIZE_CLASSES[size], ACCENT_CLASSES[accent], className]
          .filter(Boolean)
          .join(' ')}
        {...props}
      />
    );
  }
);

UiCheckbox.displayName = 'UiCheckbox';
